// src/pages/employeeCommission/components/EmployeeLevelBulkModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { getUsers } from '../../../services/api/users';
import { bulkUpsertCommissionConfigs } from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const EMPTY_ROW = () => ({
  userId:            '',
  confirmCommission: '',
  createCommission:  '',
  calculationBasis:  'order_count',
  startDate:         '',
  timeRange:         'monthly',
});

const EmployeeLevelBulkModal = ({ onClose, onSaved, existingConfigs = [] }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const isEdit = existingConfigs.length > 0;
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState(
    isEdit
      ? existingConfigs.map((c) => ({
          userId:            c.userId,
          confirmCommission: c.confirmCommission,
          createCommission:  c.createCommission,
          calculationBasis:  c.calculationBasis,
          startDate:         c.startDate || '',
          timeRange:         c.timeRange || 'monthly',
        }))
      : [EMPTY_ROW()]
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getUsers({ limit: 200 })
      .then((res) => setUsers(res?.users || []))
      .catch(() => {});
  }, []);

  const addRow    = () => setRows([...rows, EMPTY_ROW()]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, field, value) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const basisLabel = (key) => {
    const map = {
      order_count:    t('employeeCommission.orderCount'),
      order_quantity: t('employeeCommission.orderQuantity'),
      order_value:    t('employeeCommission.orderValue'),
    };
    return map[key] || key;
  };

  const handleSave = async () => {
    const valid = rows.every(
      (r) => r.userId && r.confirmCommission !== '' && r.createCommission !== ''
    );
    if (!valid) {
      showError('Please fill in all fields for each row.');
      return;
    }

    setSaving(true);
    try {
      const configs = rows.map((r) => ({
        userId:            parseInt(r.userId),
        productId:         null,
        confirmCommission: parseFloat(r.confirmCommission),
        createCommission:  parseFloat(r.createCommission),
        calculationBasis:  r.calculationBasis,
        startDate:         r.startDate || null,
        timeRange:         r.timeRange || 'monthly',
      }));
      await bulkUpsertCommissionConfigs(configs);
      success(t('employeeCommission.bulkConfigSaved'));
      onSaved();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalLarge}`}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>{t('employeeCommission.bulkEmployeeConfig')}</h2>
            <p className={styles.modalSubtitle}>
              {isEdit
                ? t('employeeCommission.bulkEditDescription')
                : t('employeeCommission.bulkAddDescription')}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.bulkTable}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Basis</th>
                  <th>Confirm Commission</th>
                  <th>Create Commission</th>
                  <th>Period</th>
                  <th>Start Date</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <select
                        value={row.userId}
                        onChange={(e) => updateRow(idx, 'userId', e.target.value)}
                        style={{ minWidth: 180 }}
                      >
                        <option value="">-- Select --</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} {u.employeeNumber ? `(${u.employeeNumber})` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={row.calculationBasis}
                        onChange={(e) => updateRow(idx, 'calculationBasis', e.target.value)}
                        style={{ minWidth: 130 }}
                      >
                        {['order_count', 'order_quantity', 'order_value'].map((v) => (
                          <option key={v} value={v}>
                            {basisLabel(v)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.confirmCommission}
                        onChange={(e) => updateRow(idx, 'confirmCommission', e.target.value)}
                        placeholder="0.00"
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.createCommission}
                        onChange={(e) => updateRow(idx, 'createCommission', e.target.value)}
                        placeholder="0.00"
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <select
                        value={row.timeRange}
                        onChange={(e) => updateRow(idx, 'timeRange', e.target.value)}
                        style={{ minWidth: 120 }}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="date"
                        value={row.startDate}
                        onChange={(e) => updateRow(idx, 'startDate', e.target.value)}
                        style={{ width: 140 }}
                        title="Leave blank for no restriction"
                      />
                    </td>
                    <td>
                      <button
                        className={styles.btnDanger}
                        style={{ padding: '0.375rem' }}
                        onClick={() => removeRow(idx)}
                        disabled={rows.length === 1}
                        title="Remove row"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            className={styles.btnSecondary}
            onClick={addRow}
            style={{ marginTop: '1rem' }}
          >
            <Plus size={16} />
            Add Row
          </button>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {t('employeeCommission.cancel')}
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <div className={styles.spinner} />
                {t('employeeCommission.saving')}
              </>
            ) : (
              t('employeeCommission.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLevelBulkModal;